// This file is auto-generated by @hey-api/openapi-ts

export enum BackgroundType {
  COLOR = 'color',
  IMAGE = 'image',
}

/**
 * A question group as the respondent runtime needs it: one resolved stem.
 */
export type CollectQuestionGroupResponse = {
  id: string
  kind: SignalQuestionGroupKind
  wording: string
  semantic_type: SemanticType | null
  config: {
    [key: string]: unknown
  }
  question_ids: Array<string>
}

/**
 * A question as the respondent runtime needs it: one resolved wording string.
 */
export type CollectQuestionResponse = {
  id: string
  order: number
  semantic_type: SemanticType
  wording: string
  required: boolean
  /**
   * Question ids this question's show_if expression reads. Empty when there is no condition, or when the condition reads only embedded data. The collection app should await an in-flight answers PATCH before advancing to this question only when this list intersects the question ids in that PATCH; otherwise it can advance using the latest hidden_question_ids it already holds.
   */
  show_if_question_ids: Array<string>
  config: {
    [key: string]: unknown
  }
  group_id?: string | null
}

/**
 * Everything the collection app needs to render the questionnaire.
 */
export type CollectSignalResponse = {
  signal_id: string
  signal_version_id: string | null
  organization_id: number
  project_id: string
  version: number | null
  language: string
  available_languages: Array<string>
  source_language: string
  dynamic_languages: boolean
  is_preview: boolean
  redirect_url: string | null
  show_progress_bar: boolean
  voice_mode_enabled: boolean
  start_screen_title: string | null
  /**
   * Closing message for the session language. Null when the Signal has no custom closing message.
   */
  closing_message: string | null
  branding: SignalBranding
  /**
   * Rough respondent completion time in seconds.
   */
  estimated_duration: number
  embedded_data_keys: Array<string>
  questions: Array<CollectQuestionResponse>
  question_groups?: Array<CollectQuestionGroupResponse>
}

export enum SemanticType {
  OPEN_TEXT = 'open_text',
  YES_NO = 'yes_no',
  NPS = 'nps',
  CSAT = 'csat',
  CES = 'ces',
  STAR_RATING = 'star_rating',
  LIKERT = 'likert',
  GENERIC_SCORE = 'generic_score',
  SCALE = 'scale',
  NUMERIC = 'numeric',
  SINGLE_SELECT = 'single_select',
  MULTI_SELECT = 'multi_select',
  EMAIL = 'email',
  COUNTRY = 'country',
  DATE = 'date',
  DATETIME = 'datetime',
  MESSAGE = 'message',
}

export type SignalBackground = {
  /**
   * Whether the collection UI shows the color only, or the image with the overlay color laid over it.
   */
  type?: BackgroundType
  /**
   * Page background; also shown in image mode until an image exists.
   */
  color?: string
  /**
   * Background image, used when type is image. Must be a URL returned by the user-attachments upload endpoint.
   */
  image_url?: string | null
  /**
   * Color laid over the image; only applies in image mode.
   */
  overlay_color?: string
  /**
   * Overlay opacity in percent; only applies in image mode.
   */
  overlay_opacity?: number
}

/**
 * How the collection UI renders a Signal: logo, accent color and background.
 */
export type SignalBranding = {
  logo_url: string | null
  primary_color: string
  background: SignalBackground
}

/**
 * Concrete group kinds. Bare groups are not creatable.
 */
export enum SignalQuestionGroupKind {
  MATRIX = 'matrix',
  SINGLE_SELECT_PLUS_OTHER = 'single_select_plus_other',
  MULTI_SELECT_PLUS_OTHER = 'multi_select_plus_other',
}

/**
 * Open/resume also hands out the bearer token for subsequent calls.
 */
export type CollectSessionOpenedResponse = {
  id: string
  signal_id: string
  signal_version_id: string
  status: SessionStatus
  is_test: boolean
  resume_token: string | null
  language: string | null
  answers: {
    [key: string]: unknown
  }
  /**
   * Question and group ids mapped to localized wording with {{ embedded["<key>"] }} and {{ answers["<question_id>"] }} substituted. Choice, likert, and yes/no answers use their labels. Multi-select and country answers are comma-separated labels. A missing or empty answer renders as a translated "(no answer)". A missing embedded key renders as a translated "(not available)".
   */
  wordings: {
    [key: string]: string
  }
  /**
   * Question ids whose show_if evaluates to false for the current answers and embedded data. Keep the latest value received. When advancing, await an in-flight answers PATCH only if the next question's show_if_question_ids intersects the patched question ids; otherwise advance immediately. Embedded-only conditions are already reflected here after open.
   */
  hidden_question_ids: Array<string>
  started_at: string
  last_activity_at: string
  finalized_at: string | null
  access_token: string
}

export enum SessionStatus {
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  PARTIAL = 'partial',
}

export type CollectSessionOpenRequest = {
  resume_token?: string | null
  embedded_data?: {
    [key: string]: unknown
  }
  respondent_identifier?: string | null
  language?: string | null
  timezone?: string | null
}

/**
 * Respondent-facing session state — no ingest internals.
 */
export type CollectSessionResponse = {
  id: string
  signal_id: string
  signal_version_id: string
  status: SessionStatus
  is_test: boolean
  resume_token: string | null
  language: string | null
  answers: {
    [key: string]: unknown
  }
  /**
   * Question and group ids mapped to localized wording with {{ embedded["<key>"] }} and {{ answers["<question_id>"] }} substituted. Choice, likert, and yes/no answers use their labels. Multi-select and country answers are comma-separated labels. A missing or empty answer renders as a translated "(no answer)". A missing embedded key renders as a translated "(not available)".
   */
  wordings: {
    [key: string]: string
  }
  /**
   * Question ids whose show_if evaluates to false for the current answers and embedded data. Keep the latest value received. When advancing, await an in-flight answers PATCH only if the next question's show_if_question_ids intersects the patched question ids; otherwise advance immediately. Embedded-only conditions are already reflected here after open.
   */
  hidden_question_ids: Array<string>
  started_at: string
  last_activity_at: string
  finalized_at: string | null
}

export type CollectAnswersPatchRequest = {
  answers: {
    [key: string]: unknown
  }
  /**
   * How each patched answer was entered. A missing key keeps the stored mode; reporting the other mode later folds that question to mixed.
   */
  input_modes?: {
    [key: string]: InputMode
  }
}

/**
 * How a question was answered. ``mixed`` means both of the others were used.
 */
export enum InputMode {
  AUDIO = 'audio',
  TYPED = 'typed',
  MIXED = 'mixed',
}

/**
 * Session state after a probing turn, plus the follow-up to show next.
 */
export type CollectProbeResponse = {
  id: string
  signal_id: string
  signal_version_id: string
  status: SessionStatus
  is_test: boolean
  resume_token: string | null
  language: string | null
  answers: {
    [key: string]: unknown
  }
  /**
   * Question and group ids mapped to localized wording with {{ embedded["<key>"] }} and {{ answers["<question_id>"] }} substituted. Choice, likert, and yes/no answers use their labels. Multi-select and country answers are comma-separated labels. A missing or empty answer renders as a translated "(no answer)". A missing embedded key renders as a translated "(not available)".
   */
  wordings: {
    [key: string]: string
  }
  /**
   * Question ids whose show_if evaluates to false for the current answers and embedded data. Keep the latest value received. When advancing, await an in-flight answers PATCH only if the next question's show_if_question_ids intersects the patched question ids; otherwise advance immediately. Embedded-only conditions are already reflected here after open.
   */
  hidden_question_ids: Array<string>
  started_at: string
  last_activity_at: string
  finalized_at: string | null
  follow_up: string | null
  done: boolean
  turns: Array<CollectProbeTurnResponse>
}

export type CollectProbeTurnResponse = {
  role: 'agent' | 'respondent'
  text: string
}

export type CollectProbeRequest = {
  answer: string
  /**
   * How this turn was entered. Omitted leaves the question's mode unchanged.
   */
  input_mode?: InputMode | null
}

export type ApiException422 = {
  /**
   * Type of the error returned.
   */
  type: 'validation_error'
  /**
   * Short string that uniquely identifies the reason for this error. Useful for programmatic error handling.
   */
  code: 'signals.voice_input.audio_too_large' | 'signals.voice_input.audio_unsupported' | 'signals.voice_input.failed'
  /**
   * Brief human-readable message providing more details about the error that has occurred. Can be shown to end users.
   */
  message: string
  /**
   * Lengthier human-readable explanation of the error. This property is intended for use by developers only and provides additional information on how this issue could be resolved.
   */
  details?: string | null
  /**
   * Optional correlation IDs that provide additional context for debugging purposes.
   */
  correlation_ids?: {
    [key: string]: unknown
  } | null
  /**
   * Context that contains additional information about why this error occurred specifically. It is always an object, but its properties differ depending on the `code` field. For a given error with the same `code` fields, its object structure is equivalent.
   */
  context?: {
    [key: string]: unknown
  } | null
}

export type ApiException404 = {
  /**
   * Type of the error returned.
   */
  type: 'not_found_error'
  /**
   * Short string that uniquely identifies the reason for this error. Useful for programmatic error handling.
   */
  code: 'signals.session.not_found' | 'signals.voice_input.question_not_found'
  /**
   * Brief human-readable message providing more details about the error that has occurred. Can be shown to end users.
   */
  message: string
  /**
   * Lengthier human-readable explanation of the error. This property is intended for use by developers only and provides additional information on how this issue could be resolved.
   */
  details?: string | null
  /**
   * Optional correlation IDs that provide additional context for debugging purposes.
   */
  correlation_ids?: {
    [key: string]: unknown
  } | null
  /**
   * Context that contains additional information about why this error occurred specifically. It is always an object, but its properties differ depending on the `code` field. For a given error with the same `code` fields, its object structure is equivalent.
   */
  context?: {
    [key: string]: unknown
  } | null
}

export type LiveTranscriptionSessionResponse = {
  session_id: string
  stream_ticket: string
  stream_ticket_expires_in_s: number
}

/**
 * The single-use stream ticket minted by ``start``, as a query parameter.
 *
 * ``stream`` is consumed by ``EventSource``, which cannot set headers, so its
 * credential has to ride in the URL — where access logs, proxies and error
 * trackers keep it. The session's own access token would be far too valuable to
 * leave there, hence the short-lived ticket; the sibling endpoints take the
 * access token in the ``X-Session-Token`` header instead.
 */
export type LiveTranscriptionStreamRequest = {
  stream_ticket: string
}

export type VoiceInputResponse = {
  /**
   * Question the recording was an answer to.
   */
  question_key: string
  /**
   * The answer extracted from the recording, or null if the recording did not answer the question.
   */
  value?: unknown
  /**
   * Whether the recording answered the question.
   */
  answered?: boolean
  /**
   * Sentence to read back to the respondent when the recording did not answer the question. Null when it did, and also when there is nothing usable in the recording and nothing to say about it.
   */
  feedback?: string | null
  /**
   * Transcript of the recording, empty if none was produced.
   */
  transcript?: string
}

export type CollectSignalRuntimeData = {
  body?: never
  path: {
    link_token: string
  }
  query?: never
  url: '/signals/{link_token}'
}

export type CollectSignalRuntimeResponses = {
  /**
   * OK
   */
  200: CollectSignalResponse
}

export type CollectSignalRuntimeResponse = CollectSignalRuntimeResponses[keyof CollectSignalRuntimeResponses]

export type CollectSessionOpenData = {
  body: CollectSessionOpenRequest
  path: {
    link_token: string
  }
  query?: never
  url: '/signals/{link_token}/sessions'
}

export type CollectSessionOpenResponses = {
  /**
   * OK
   */
  200: CollectSessionOpenedResponse
}

export type CollectSessionOpenResponse = CollectSessionOpenResponses[keyof CollectSessionOpenResponses]

export type CollectSessionDetailData = {
  body?: never
  headers?: {
    'X-Session-Token'?: string
  }
  path: {
    session_id: string
  }
  query?: never
  url: '/sessions/{session_id}'
}

export type CollectSessionDetailResponses = {
  /**
   * OK
   */
  200: CollectSessionResponse
}

export type CollectSessionDetailResponse = CollectSessionDetailResponses[keyof CollectSessionDetailResponses]

export type CollectSessionPatchAnswersData = {
  body: CollectAnswersPatchRequest
  headers?: {
    'X-Session-Token'?: string
  }
  path: {
    session_id: string
  }
  query?: never
  url: '/sessions/{session_id}/answers'
}

export type CollectSessionPatchAnswersResponses = {
  /**
   * OK
   */
  200: CollectSessionResponse
}

export type CollectSessionPatchAnswersResponse =
  CollectSessionPatchAnswersResponses[keyof CollectSessionPatchAnswersResponses]

export type CollectSessionProbeQuestionData = {
  body: CollectProbeRequest
  headers?: {
    'X-Session-Token'?: string
  }
  path: {
    session_id: string
    question_id: string
  }
  query?: never
  url: '/sessions/{session_id}/questions/{question_id}/probe'
}

export type CollectSessionProbeQuestionResponses = {
  /**
   * OK
   */
  200: CollectProbeResponse
}

export type CollectSessionProbeQuestionResponse =
  CollectSessionProbeQuestionResponses[keyof CollectSessionProbeQuestionResponses]

export type CollectSessionFinalizeData = {
  body?: never
  headers?: {
    'X-Session-Token'?: string
  }
  path: {
    session_id: string
  }
  query?: never
  url: '/sessions/{session_id}/finalize'
}

export type CollectSessionFinalizeResponses = {
  /**
   * OK
   */
  200: CollectSessionResponse
}

export type CollectSessionFinalizeResponse = CollectSessionFinalizeResponses[keyof CollectSessionFinalizeResponses]

export type CollectSessionSealPartialData = {
  body?: never
  headers?: {
    'X-Session-Token'?: string
  }
  path: {
    session_id: string
  }
  query?: never
  url: '/sessions/{session_id}/seal-partial'
}

export type CollectSessionSealPartialResponses = {
  /**
   * OK
   */
  200: CollectSessionResponse
}

export type CollectSessionSealPartialResponse =
  CollectSessionSealPartialResponses[keyof CollectSessionSealPartialResponses]

export type CollectLiveTranscriptionStartData = {
  body?: never
  headers: {
    'X-Session-Token': string
  }
  path: {
    session_id: string
  }
  query?: never
  url: '/sessions/{session_id}/live-transcription/start'
}

export type CollectLiveTranscriptionStartErrors = {
  /**
   * Not Found
   */
  404: ApiException404
  /**
   * Unprocessable Entity
   */
  422: ApiException422
}

export type CollectLiveTranscriptionStartError =
  CollectLiveTranscriptionStartErrors[keyof CollectLiveTranscriptionStartErrors]

export type CollectLiveTranscriptionStartResponses = {
  /**
   * OK
   */
  200: LiveTranscriptionSessionResponse
}

export type CollectLiveTranscriptionStartResponse =
  CollectLiveTranscriptionStartResponses[keyof CollectLiveTranscriptionStartResponses]

export type CollectLiveTranscriptionStreamData = {
  body?: never
  path: {
    session_id: string
  }
  query: {
    stream_ticket: string
  }
  url: '/sessions/{session_id}/live-transcription/stream'
}

export type CollectLiveTranscriptionStreamErrors = {
  /**
   * Not Found
   */
  404: ApiException404
  /**
   * Unprocessable Entity
   */
  422: ApiException422
}

export type CollectLiveTranscriptionStreamError =
  CollectLiveTranscriptionStreamErrors[keyof CollectLiveTranscriptionStreamErrors]

export type CollectLiveTranscriptionAudioData = {
  body: Blob | File
  headers: {
    'X-Session-Token': string
    'X-Signal-Question': string
    'X-Signal-Turn'?: number
  }
  path: {
    session_id: string
  }
  query?: never
  url: '/sessions/{session_id}/live-transcription/audio'
}

export type CollectLiveTranscriptionAudioErrors = {
  /**
   * Not Found
   */
  404: ApiException404
  /**
   * Unprocessable Entity
   */
  422: ApiException422
}

export type CollectLiveTranscriptionAudioError =
  CollectLiveTranscriptionAudioErrors[keyof CollectLiveTranscriptionAudioErrors]

export type CollectLiveTranscriptionAudioResponses = {
  /**
   * Accepted
   */
  202: unknown
}

export type CollectLiveTranscriptionCloseData = {
  body?: never
  headers: {
    'X-Session-Token': string
  }
  path: {
    session_id: string
  }
  query?: never
  url: '/sessions/{session_id}/live-transcription/close'
}

export type CollectLiveTranscriptionCloseErrors = {
  /**
   * Not Found
   */
  404: ApiException404
  /**
   * Unprocessable Entity
   */
  422: ApiException422
}

export type CollectLiveTranscriptionCloseError =
  CollectLiveTranscriptionCloseErrors[keyof CollectLiveTranscriptionCloseErrors]

export type CollectLiveTranscriptionCloseResponses = {
  /**
   * No Content
   */
  204: void
}

export type CollectLiveTranscriptionCloseResponse =
  CollectLiveTranscriptionCloseResponses[keyof CollectLiveTranscriptionCloseResponses]

export type CollectVoiceInputData = {
  body: Blob | File
  headers: {
    'X-Session-Token': string
    'X-Signal-Question': string
  }
  path: {
    session_id: string
  }
  query?: never
  url: '/sessions/{session_id}/voice-input'
}

export type CollectVoiceInputErrors = {
  /**
   * Not Found
   */
  404: ApiException404
  /**
   * Unprocessable Entity
   */
  422: ApiException422
}

export type CollectVoiceInputError = CollectVoiceInputErrors[keyof CollectVoiceInputErrors]

export type CollectVoiceInputResponses = {
  /**
   * OK
   */
  200: VoiceInputResponse
}

export type CollectVoiceInputResponse = CollectVoiceInputResponses[keyof CollectVoiceInputResponses]

export type ClientOptions = {
  baseURL: 'https://api.dev.caplena.dev/api/collection/v1' | (string & {})
}
